import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import loginSideImg from "../../assets/login-screen.png";
import enphaseLogoWithText from "../../assets/enphase_logo_with_text.png";
import useAuth from "./hooks/useAuth";
import { TEXT_LIBRARY } from "../../lib/utils/constants";
import SnackbarUtils from "../../lib/components/Snackbar/utils";
import { SIGNUP_URL, FORGOT_PASSWORD_URL, PRIVACY_URL, TERMS_URL } from "./constants";
import { getCopyrightText } from "../../lib/utils/utility";

const LOGIN_ATTEMPTS_KEY = "iqevse_login_attempts";
const MAX_LOGIN_ATTEMPTS = 5;
const LOGIN_LOCKOUT_MS = 15 * 60 * 1000;

function getLoginLockoutRemainingMs(now = Date.now()) {
  try {
    const { lockedUntil = 0 } = JSON.parse(sessionStorage.getItem(LOGIN_ATTEMPTS_KEY) || "{}");
    return Math.max(0, Number(lockedUntil) - now);
  } catch {
    return 0;
  }
}

function recordLoginFailure(retryAfterSeconds) {
  const now = Date.now();
  let count = 0;
  let lockedUntil = 0;
  try {
    const stored = JSON.parse(sessionStorage.getItem(LOGIN_ATTEMPTS_KEY) || "{}");
    count = Number(stored.count) || 0;
    lockedUntil = Number(stored.lockedUntil) || 0;
  } catch {
    /* use defaults */
  }
  count += 1;
  const lockMs =
    retryAfterSeconds != null && !Number.isNaN(Number(retryAfterSeconds))
      ? Number(retryAfterSeconds) * 1000
      : LOGIN_LOCKOUT_MS;
  if (count >= MAX_LOGIN_ATTEMPTS || typeof retryAfterSeconds === "number") {
    lockedUntil = now + lockMs;
  }
  sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify({ count, lockedUntil }));
  return getLoginLockoutRemainingMs(now);
}

function clearLoginAttempts() {
  sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
}

function getLockoutMessage(remainingMs) {
  const minutes = Math.max(1, Math.ceil(remainingMs / 60000));
  return TEXT_LIBRARY.LOGIN_TOO_MANY_ATTEMPTS.replace("{duration}", `${minutes} minute${minutes === 1 ? "" : "s"}`);
}

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [lockoutRemainingMs, setLockoutRemainingMs] = useState(() => getLoginLockoutRemainingMs());
  const { login, loading } = useAuth();

  const lockedOut = lockoutRemainingMs > 0;

  const refreshLockout = useCallback(() => {
    setLockoutRemainingMs(getLoginLockoutRemainingMs());
  }, []);

  useEffect(() => {
    if (!lockedOut) return undefined;
    const intervalId = setInterval(refreshLockout, 1000);
    return () => clearInterval(intervalId);
  }, [lockedOut, refreshLockout]);

  const handleSubmit = async e => {
    e.preventDefault();

    const remainingMs = getLoginLockoutRemainingMs();
    if (remainingMs > 0) {
      SnackbarUtils.error(getLockoutMessage(remainingMs));
      refreshLockout();
      return;
    }

    try {
      const formData = { email: email, password: password, locale: "en" };
      const data = await login(formData);
      clearLoginAttempts();
      refreshLockout();
      let systemId = data?.system_id || data?.user?.system_id || "";
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const startDate1 = `${yyyy}-${mm}-${dd}`;
      const endDate1 = `${yyyy}-${mm}-${dd}`;
      const params = new URLSearchParams({
        systemId,
        startDate1,
        endDate1,
      }).toString();
      navigate(`/?${params}`, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const headers = err.response?.headers;
      const retryAfter = headers?.["retry-after"] ?? headers?.["Retry-After"];

      const lockRemaining =
        status === 429
          ? recordLoginFailure(
              retryAfter != null && !Number.isNaN(Number(retryAfter)) ? Number(retryAfter) : LOGIN_LOCKOUT_MS / 1000
            )
          : recordLoginFailure();

      refreshLockout();

      if (lockRemaining > 0) {
        SnackbarUtils.error(getLockoutMessage(lockRemaining));
        return;
      }

      // SECURITY FIX M-5: Generic error messages to prevent information disclosure
      let errorMessage = TEXT_LIBRARY.INVALID_CREDENTIALS;

      // Only show generic error to user
      if (err.response?.data?.status === "UNAUTHORIZED") {
        errorMessage = TEXT_LIBRARY.INVALID_CREDENTIALS;
      } else if (status === 429) {
        errorMessage = TEXT_LIBRARY.LOGIN_TOO_MANY_ATTEMPTS.replace("{duration}", "15 minutes");
      } else {
        errorMessage = TEXT_LIBRARY.INVALID_CREDENTIALS;
      }

      // Log detailed error for debugging (development only)
      if (process.env.NODE_ENV === "development") {
        console.error("Login error details:", err.response?.data || err.message);
      }

      SnackbarUtils.error(`${errorMessage}`);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden w-full">
      {/* Left: Image panel */}
      <div className="hidden md:flex flex-1 relative overflow-hidden items-center justify-center">
        <img src={loginSideImg} alt="ev charger" className="w-full h-full object-cover absolute inset-0" />
        <div className="relative bg-white/75 backdrop-blur-sm rounded-xl p-6 max-w-[380px] shadow-lg flex gap-4 items-start ml-10">
          <div className="w-7 h-7 rounded-full border-4 border-[#3ec28f] mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-bold text-base text-gray-900 mb-1.5 leading-snug">
              Efficient EV Fleet Charging &<br />
              Management Platform
            </div>
            <div className="text-gray-600 text-sm leading-relaxed">
              Monitor, control, and optimize your electric vehicle charging infrastructure from a single dashboard.
            </div>
          </div>
        </div>
      </div>

      <div
        className="flex flex-col justify-between bg-white py-10 px-14"
        style={{ width: 480, minWidth: 400, maxWidth: 500 }}
      >
        <div className="flex flex-col justify-center flex-1">
          <div className="mb-10">
            <img src={enphaseLogoWithText} alt="Enphase" className="h-7 w-auto object-contain" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome!</h1>
          <p className="text-gray-500 text-sm mb-10 leading-relaxed">
            Sign in to your EV Fleet Management account using your email and password.
          </p>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
              required
              className="px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm outline-none transition-all focus:ring-2 focus:ring-[#3ec28f] focus:border-transparent"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 bg-gray-50 text-sm outline-none transition-all focus:ring-2 focus:ring-[#3ec28f] focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer focus:outline-none transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="flex justify-between items-center">
              <a
                href={SIGNUP_URL}
                className="text-[#3ec28f] text-sm font-medium no-underline cursor-pointer hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sign Up
              </a>
              <a
                href={FORGOT_PASSWORD_URL}
                className="text-[#3ec28f] text-sm font-medium no-underline cursor-pointer hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Forgot Password?
              </a>
            </div>
            <button
              type="submit"
              className="bg-gray-900 text-white rounded-lg py-3 px-4 text-sm font-semibold mt-1 hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || lockedOut}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
            {lockedOut && (
              <p className="text-sm text-amber-700 -mt-2" role="status">
                {getLockoutMessage(lockoutRemainingMs)}
              </p>
            )}
          </form>
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-gray-100 mt-6">
          <span className="text-gray-400 text-xs">{getCopyrightText()}</span>
          <div className="flex items-center gap-2 text-xs">
            <a
              href={PRIVACY_URL}
              className="text-gray-400 hover:text-gray-600 no-underline hover:underline transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy
            </a>
            <span className="text-gray-300">|</span>
            <a
              href={TERMS_URL}
              className="text-gray-400 hover:text-gray-600 no-underline hover:underline transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
